import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import type { TradableAreaIssuer } from '@ebalance/proto/bffuser/v1/tradable_area_service_pb'
import i18next from 'i18next'

/**
 * 発注主体区分 (需要BG / 発電 / 自己託送BG) のセレクタに関する純粋ヘルパ。
 *
 * 選択肢の出所は取引可能エリア RPC (`TradableAreaService.ListTradableAreas`) の
 * `issuers` で、 受渡日時点の広域マスタから backend が求める (区分ごとに、 受渡契約
 * コードを解決できるエリアとコード = `registered_areas`、 そのうち選べるエリア =
 * `tradable_areas`)。 どの区分を出すか・どのエリアが選べるかの判定は backend が持ち、
 * ここでは応答 → 選択肢への変換 (並び) と、 選択中の区分でのエリアの見せ方だけを担う。
 *
 * 札は発注主体を区分 (`issuer_type` = `LicenseType`) だけで持つので、 選択値も区分。
 */

/**
 * 発注主体区分の優先順 (spec AC-BSP-005d / AC-BIN-004「需要BG＞発電＞自己託送BG」)。
 * 選択肢の並びと既定選択 (= 先頭) に使う。 DR は発注主体区分に含めない (P0 スコープ外)。
 */
const ISSUER_TYPE_PRIORITY: readonly LicenseType[] = [
  LicenseType.RETAIL,
  LicenseType.GENERATION,
  LicenseType.SELF_WHEELING,
]

/**
 * 発注主体区分の表示ラベル (需要BG / 発電 / 自己託送BG)。 spec AC-BSP-005d / AC-BIN-004 と
 * design `bid-issuer-selector-v2.js` の `ISSUER_LABELS` に合わせる。
 */
export const issuerTypeLabel = (issuerType: LicenseType): string => {
  const t = i18next.t.bind(i18next)
  switch (issuerType) {
    case LicenseType.RETAIL:
      return t('page:biddingCommon.issuerSelector.type.retail')
    case LicenseType.GENERATION:
      return t('page:biddingCommon.issuerSelector.type.generation')
    case LicenseType.SELF_WHEELING:
      return t('page:biddingCommon.issuerSelector.type.selfWheeling')
    default:
      return ''
  }
}

/** 発注主体プルダウンの選択肢 1 件 (= 発注主体区分 1 つ)。 */
export type IssuerOption = {
  /** 選択値。 札の `issuer_type` にそのまま載せる。 */
  issuerType: LicenseType
  /**
   * 受渡日に受渡契約コードを解決できるエリア (= 広域マスタに登録済み) と、 その
   * **受渡契約コード** (需要BG / 自己託送BG なら BG コード、 発電なら計画提出者コード)。
   * ここに無いエリアは「広域マスタ未登録」 (AC-BSP-005b / AC-BIN-004 ※)。 JEPX 会員情報が
   * 未設定の組織の発電は、 自社の事業者の計画提出者のエリアがコード空で入る (AC-IJM-010)。
   */
  registeredAreas: Array<{ area: AreaCode; code: string }>
  /**
   * [[registeredAreas]] のうち選べるエリア。 登録済みでもここに無いエリア (JEPX 会員情報が
   * 未設定、 送信先の受渡契約コードが無い等) は選択肢に残したまま選べない。
   */
  tradableAreas: AreaCode[]
}

/**
 * 取引可能エリア RPC の `issuers` を発注主体プルダウンの選択肢に変換する。
 *
 * - 区分の集合は backend が決める (広域マスタに登録済みのエリアが 1 つも無い区分は
 *   応答に含まれない。 AC-BSP-005d / AC-BIN-004)。 0 件なら札作成をブロックする
 *   (AC-BSP-005d / AC-BIN-056。 呼び出し側が選択肢 0 件で判定する)
 * - 並びは区分の優先順 (需要BG＞発電＞自己託送BG)。 先頭が既定選択になる
 *   ([[IssuerSelector]] の `selectsDefault`)。 優先順に無い区分は出さない
 */
export const buildIssuerOptions = (
  issuers: readonly TradableAreaIssuer[],
): IssuerOption[] =>
  issuers
    .filter((i) => ISSUER_TYPE_PRIORITY.includes(i.issuerType))
    .map((i) => ({
      issuerType: i.issuerType,
      registeredAreas: i.registeredAreas.map((r) => ({
        area: r.area,
        code: r.deliveryContractCode,
      })),
      tradableAreas: [...i.tradableAreas],
    }))
    .sort(
      (a, b) =>
        ISSUER_TYPE_PRIORITY.indexOf(a.issuerType) -
        ISSUER_TYPE_PRIORITY.indexOf(b.issuerType),
    )

/** Toolbar registration is independent of the delivery date (AC-BSP-135).
 * Area availability still comes from the selected delivery date.
 */
export const buildRegisteredIssuerOptions = (
  registeredTypes: readonly LicenseType[],
  issuers: readonly TradableAreaIssuer[],
): IssuerOption[] => {
  const datedOptions = buildIssuerOptions(issuers)
  return ISSUER_TYPE_PRIORITY.filter((type) =>
    registeredTypes.includes(type),
  ).map(
    (issuerType) =>
      datedOptions.find((option) => option.issuerType === issuerType) ?? {
        issuerType,
        registeredAreas: [],
        tradableAreas: [],
      },
  )
}

/** 選択肢群から区分の 1 件を引く。 未選択 (UNSPECIFIED) / 該当なしは undefined。 */
export const findIssuerOption = (
  options: readonly IssuerOption[],
  issuerType: LicenseType,
): IssuerOption | undefined =>
  issuerType === LicenseType.UNSPECIFIED
    ? undefined
    : options.find((o) => o.issuerType === issuerType)

/** 開いたモーダルで選ばれる区分。 未選択なら [[IssuerSelector]] が既定選択する先頭の区分 (#10105)。 */
export const settledIssuerOption = (
  options: readonly IssuerOption[],
  issuerType: LicenseType,
): IssuerOption | undefined =>
  issuerType === LicenseType.UNSPECIFIED
    ? options[0]
    : findIssuerOption(options, issuerType)

/**
 * 選択中の発注主体区分でのエリアの扱い (AC-BSP-005b / AC-BIN-004 ※)。
 *
 * - `selectable`: 選べる
 * - `unregistered`: 受渡契約コードを解決できない (= 広域マスタ未登録)。 札作成モーダルの
 *   選択肢には出さない ([[issuerAreaChoices]])
 * - `notTradable`: 登録済みだが選べない (JEPX 会員情報が未設定・送信先の受渡契約コードが
 *   無い等)。 選択肢に残して選択不可にし、 [[issuerAreaOptionLabel]] は補助テキストを付けない
 *   — 「広域マスタ未登録」 を付けると原因でないマスタを探させるため。 スポット入札画面上部の
 *   エリアフィルタは発注可否ではなく登録集合で決め、 登録済みのエリアは選べる (AC-IJM-013。
 *   `pageIssuerScope` の `areaFilterAvailability`)
 *
 * 区分が未選択 (undefined) のうちは絞り込まない (`selectable`)。
 */
export type IssuerAreaAvailability =
  | 'selectable'
  | 'unregistered'
  | 'notTradable'

export const issuerAreaAvailability = (
  option: IssuerOption | undefined,
  area: AreaCode,
): IssuerAreaAvailability => {
  if (option === undefined) return 'selectable'
  if (!option.registeredAreas.some((r) => r.area === area)) {
    return 'unregistered'
  }
  if (!option.tradableAreas.includes(area)) return 'notTradable'
  return 'selectable'
}

/**
 * 選択中の区分 × エリアで backend が解決した受渡契約コード (札作成モーダルが読み取り専用で出す。
 * AC-BIN-004)。 解決は backend が済ませた `registeredAreas` を引くだけで、 売買の向きは使わない
 * (売り・買いを問わず区分で決まる。 #9613)。 区分・エリアが未選択、 エリアが未登録、 解決した
 * コードが空 (JEPX 会員情報が未設定の発電) の時は undefined。
 */
export const issuerAreaDeliveryContractCode = (
  option: IssuerOption | undefined,
  area: AreaCode | undefined,
): string | undefined => {
  if (option === undefined || area === undefined) return undefined
  const code = option.registeredAreas.find((r) => r.area === area)?.code
  return code === undefined || code === '' ? undefined : code
}

export type IssuerAreaChoice = {
  area: AreaCode
  availability: IssuerAreaAvailability
}

/** 札を作るモーダルのエリア選択肢。 広域マスタ未登録のエリアは出さず、 選択中のエリアだけ残す (#10105)。 */
export const issuerAreaChoices = (
  option: IssuerOption | undefined,
  areas: readonly AreaCode[],
  selectedArea: AreaCode | undefined,
): IssuerAreaChoice[] =>
  areas
    .map((area) => ({
      area,
      availability: issuerAreaAvailability(option, area),
    }))
    .filter(
      ({ area, availability }) =>
        availability !== 'unregistered' || area === selectedArea,
    )

/** 札作成モーダルのエリア欄 (Controller) の未選択。 `undefined` で空けると欄の mount 時の値が表示に残る (#10105)。 */
export const AREA_UNSELECTED = AreaCode.UNSPECIFIED

/** エリア欄の値。 未選択 ([[AREA_UNSELECTED]]) は undefined にする。 */
export const selectedAreaOf = (
  value: AreaCode | undefined,
): AreaCode | undefined => (value === AREA_UNSELECTED ? undefined : value)

/** `area` を選択中の発注主体区分で選べるか (区分が未選択なら絞り込まない)。 */
export const isAreaSelectableForIssuer = (
  option: IssuerOption | undefined,
  area: AreaCode,
): boolean => issuerAreaAvailability(option, area) === 'selectable'

/** 広域マスタ未登録で選べないエリアに添える補助テキスト (AC-BSP-005b)。 */
export const issuerAreaUnregisteredText = (): string =>
  i18next.t('page:biddingCommon.issuerSelector.areaUnregistered')

/**
 * エリア選択肢のラベル。 広域マスタ未登録のエリアだけ補助テキストを添える
 * (登録済みで選べないエリアには付けない。 [[IssuerAreaAvailability]])。
 */
export const issuerAreaOptionLabel = (
  areaLabel: string,
  availability: IssuerAreaAvailability,
): string =>
  availability === 'unregistered'
    ? `${areaLabel} (${issuerAreaUnregisteredText()})`
    : areaLabel

/** 業務エリア設定に含まれないエリアのラベル (AC-BSP-005b / AC-BIN-004 ※)。 */
export const areaOutsideBusinessAreasLabel = (areaLabel: string): string =>
  `${areaLabel} (${i18next.t('page:biddingCommon.issuerSelector.areaOutsideBusinessAreas')})`

export type IssuerAreaSelectOption = {
  area: AreaCode
  label: string
  disabled: boolean
}

/**
 * 札を作るモーダルのエリア選択肢 (AC-BSP-005b / AC-BIN-004 ※)。 広域機関に未登録のエリアは
 * [[issuerAreaChoices]] のとおり出さず、 登録済みで業務エリア設定に含まれないエリアは残して
 * 選べなくし「業務エリア対象外」 を添える。 両方に当たる選択中のエリアは未登録の理由を出す
 * (登録が業務エリア設定より根本の制約のため)。
 */
export const issuerAreaSelectOptions = (
  option: IssuerOption | undefined,
  areas: readonly AreaCode[],
  selectedArea: AreaCode | undefined,
  isOutsideBusinessAreas: (area: AreaCode) => boolean,
  areaLabelOf: (area: AreaCode) => string,
): IssuerAreaSelectOption[] =>
  issuerAreaChoices(option, areas, selectedArea).map(
    ({ area, availability }) => {
      const outside =
        availability !== 'unregistered' && isOutsideBusinessAreas(area)
      const areaLabel = areaLabelOf(area)
      return {
        area,
        label: outside
          ? areaOutsideBusinessAreasLabel(areaLabel)
          : issuerAreaOptionLabel(areaLabel, availability),
        disabled: outside || availability !== 'selectable',
      }
    },
  )
